import React, { useRef, useState } from 'react';
import { ArrowRight, Volume2, VolumeX } from 'lucide-react';

const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);

  React.useEffect(() => {
    const forcePlay = (video: HTMLVideoElement | null, shouldBeMuted: boolean) => {
      if (video) {
        video.muted = shouldBeMuted;
        video.defaultMuted = shouldBeMuted;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        if (shouldBeMuted) {
          video.setAttribute('muted', '');
        }
        
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay catch
          });
        }
      }
    };

    // Background video: ALWAYS muted for universal autoplay
    forcePlay(bgVideoRef.current, true);
    // Main video: Start muted but allow user to unmute
    forcePlay(videoRef.current, true);
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  return (
    <div id="inicio" className="relative bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
          <svg
            className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-white transform translate-x-1/2"
            fill="currentColor"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <polygon points="50,0 100,0 50,100 0,100" />
          </svg>

          <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
            <div className="sm:text-center lg:text-left">
              <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                <span className="block xl:inline">Excelencia y Control</span>{' '}
                <span className="block text-primary xl:inline">en cada detalle</span>
              </h1>
              <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                Más que un servicio, somos su garantía de éxito. Especialistas en control de accesos, gestión de eventos y servicios auxiliares con los más altos estándares de calidad y profesionalidad.
              </p>
              <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                <div className="rounded-md shadow">
                  <a
                    href="#contacto"
                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-blue-900 md:py-4 md:text-lg transition-all"
                  >
                    Solicitar Presupuesto
                  </a>
                </div>
                <div className="mt-3 sm:mt-0 sm:ml-3">
                  <a
                    href="#servicios"
                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-blue-100 hover:bg-blue-200 md:py-4 md:text-lg transition-all"
                  >
                    Nuestros Servicios <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 bg-black h-56 sm:h-72 md:h-96 lg:h-full overflow-hidden relative group">
         {/* Background blurred video - STRICTLY MUTED */}
        <div className="absolute inset-0 overflow-hidden">
             <video
              ref={bgVideoRef}
              className="w-full h-full object-cover blur-md opacity-50 scale-110"
              autoPlay
              loop
              muted
              playsInline
            >
              {/* Mobile optimized video (screens smaller than 768px) */}
              <source src="/hero-video-mobile.mp4" media="(max-width: 768px)" type="video/mp4" />
              {/* Desktop high-quality video */}
              <source src="/hero-video.mp4" type="video/mp4" />
            </video>
        </div>
        
        {/* Main video contained */}
        <video
          ref={videoRef}
          className="relative w-full h-full object-contain z-10"
          autoPlay
          loop
          muted
          playsInline
          poster="/hero-bg.jpg"
        >
          {/* Mobile optimized video (screens smaller than 768px) */}
          <source src="/hero-video-mobile.mp4" media="(max-width: 768px)" type="video/mp4" />
          {/* Desktop high-quality video */}
          <source src="/hero-video.mp4" type="video/mp4" />
          Tu navegador no soporta el tag de video.
        </video>

        {/* Audio Toggle Button - ONLY FOR MAIN VIDEO */}
        <button 
          onClick={toggleMute}
          className="absolute bottom-4 right-4 z-30 p-2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full border border-white/50 transition-all text-white"
          title={isMuted ? "Activar sonido" : "Desactivar sonido"}
        >
          {isMuted ? <VolumeX className="h-6 w-6" /> : <Volume2 className="h-6 w-6" />}
        </button>

        <div className="absolute inset-0 bg-primary mix-blend-multiply opacity-20 lg:hidden z-20 pointer-events-none"></div>
      </div>
    </div>
  );
};

export default Hero;
